import type { GridApi, RowSelectedEvent } from 'ag-grid-community';
import { useCallback, useContext, useEffect } from 'react';
import VariableFiltersContext from '../contexts/VariableFiltersContext';
import FilterPlatesContext from '../contexts/FilterPlatesContext';
import captureException from '../services/captureException';
import filterTypeCheckers from '../components/FilterPlateForm/filterTypeCheckers';
import useToFixedFilters from 'hooks/useToFixedFilters';

// Whether a row is the one the gadget's latest drill-down picked:
// true/false, or undefined when the drill can't be resolved (leave the row
// as it is). Shared by the AG grid (node.setSelected) and DataTable (row
// selection state).
export const useRowHighlightPredicate = ({
  drillField,
}: {
  drillField: string;
}) => {
  const { variableFilters } = useContext(VariableFiltersContext);
  const { drillDowns } = useContext(FilterPlatesContext);
  const toDrillDowns = useToFixedFilters();

  return useCallback(
    (data: { [key: string]: any } | undefined): boolean | undefined => {
      if (drillDowns.length === 0 || !data) {
        return false;
      }
      const lastDrillPlate = drillDowns[drillDowns.length - 1];
      const asDrills = toDrillDowns({
        plates: [lastDrillPlate],
        variableDrillDowns: variableFilters,
      });
      const lastDrill = asDrills.length > 0 ? asDrills[0] : undefined;
      if (!lastDrill) {
        return undefined;
      }
      const isLastForField = lastDrill.field === drillField;
      if (
        isLastForField &&
        filterTypeCheckers.isTextFilter(lastDrill) &&
        lastDrill.keywordValues &&
        lastDrill.keywordValues.includes(data[drillField])
      ) {
        return true;
      } else if (
        isLastForField &&
        filterTypeCheckers.isTextFilter(lastDrill) &&
        lastDrill.keywordsExists !== undefined &&
        lastDrill.keywordsExists === false &&
        data[drillField] === '(Blank)'
      ) {
        return true;
      } else if (
        isLastForField &&
        filterTypeCheckers.isDateFilter(lastDrill) &&
        lastDrill.dateRangeValues &&
        lastDrill.dateRangeValues.some(
          (v) => v.value.startDate && v.value.startDate === data['raw-date'],
        )
      ) {
        return true;
      }
      return false;
    },
    [drillDowns, drillField, toDrillDowns, variableFilters],
  );
};

const useRowHighlighter = ({
  gridApi,
  drillField,
  visId,
}: {
  gridApi?: GridApi;
  drillField: string;
  visId?: string;
}) => {
  const isRowHighlighted = useRowHighlightPredicate({ drillField });

  const highlightRows = useCallback(() => {
    if (gridApi && !gridApi.isDestroyed()) {
      gridApi.forEachNode((node) => {
        const isHighlighted = isRowHighlighted(node.data);
        if (isHighlighted !== undefined) {
          node.setSelected(isHighlighted);
        }
      });
    }
  }, [gridApi, isRowHighlighted]);

  // Yes - this insanity is required.
  // api.getDisplayedRows will also return invisible rows
  // which are in the buffer.
  // I cannot find a clear way via the API to get the
  // actually displayed fields...
  const onRowSelected = useCallback(
    (event: RowSelectedEvent) => {
      if (!visId) {
        return;
      }
      try {
        if (event.node.isSelected() && event.rowIndex !== null) {
          const rowTop = event.node.rowTop;
          const gridElem = document.getElementById(visId);
          if (!gridElem || rowTop === null) {
            return;
          }
          const scrollWindows =
            gridElem.getElementsByClassName('ag-body-viewport');
          if (scrollWindows.length === 0) {
            return;
          }
          const scrollWindow = scrollWindows[0];
          const isBelowScrollPosition =
            scrollWindow.clientHeight + scrollWindow.scrollTop < rowTop;
          const isAboveScrollPosition =
            scrollWindow.scrollTop > rowTop + (event.node.rowHeight || 0);
          if (isBelowScrollPosition || isAboveScrollPosition) {
            event.api.ensureIndexVisible(event.rowIndex, 'top');
          }
        }
      } catch (ex) {
        captureException(ex);
      }
    },
    [visId],
  );

  useEffect(() => {
    if (!gridApi) {
      return;
    }
    gridApi.addEventListener('rowSelected', onRowSelected);

    return () => {
      if (gridApi.isDestroyed()) {
        return;
      }
      gridApi.removeEventListener('rowSelected', onRowSelected);
    };
  }, [gridApi, onRowSelected]);

  useEffect(() => {
    if (!gridApi) {
      return;
    }
    gridApi.addEventListener('rowDataUpdated', highlightRows);
    highlightRows();

    return () => {
      if (gridApi.isDestroyed()) {
        return;
      }
      gridApi.removeEventListener('rowDataUpdated', highlightRows);
    };
  }, [gridApi, highlightRows]);

  return highlightRows;
};

export default useRowHighlighter;
